import { Link } from "react-router-dom";
import { ArrowRight, HelpCircle, Mail, MessageSquare } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FAQ } from "@/lib/content";
import { useSeo } from "@/lib/seo";

export default function HelpPage() {
  useSeo({
    title: "Help & FAQ | LUX",
    description: "Answers about how LUX works: verification, moderation, contact, reports and the DEMO prototype.",
  });
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-testid="help-page">
      <section className="pb-10 pt-14 sm:pt-20">
        <p className="lux-label">Help centre</p>
        <h1 className="mt-4 font-serif text-5xl leading-[1.02] sm:text-6xl" data-testid="help-title">
          Questions,<br />
          <em className="font-light text-lux-silverBright">answered calmly.</em>
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-lux-text/80">
          If something below doesn't cover what you need, use the <Link to="/report" className="lux-link">report form</Link> and choose \u201CSomething else\u201D - we read every message.
        </p>
      </section>

      <section aria-labelledby="faq-h" className="pb-10">
        <h2 id="faq-h" className="sr-only">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="lux-card divide-y divide-lux-border" data-testid="help-faq">
          {FAQ.map((item, i) => (
            <AccordionItem key={item.q} value={`faq-${i}`} className="border-b-0 px-5 sm:px-6" data-testid={`help-faq-item-${i}`}>
              <AccordionTrigger
                className="gap-4 py-5 text-left font-serif text-lg text-lux-text hover:no-underline"
                data-testid={`help-faq-trigger-${i}`}
              >
                <span className="flex items-center gap-3">
                  <HelpCircle className="h-4 w-4 shrink-0 text-lux-silver" aria-hidden="true" />
                  {item.q}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-5 pl-7 pr-2 text-[15px] leading-relaxed text-lux-muted">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="mt-10 grid gap-4 sm:grid-cols-2">
        <Link
          to="/safety"
          className="lux-card group block p-6 transition-colors hover:border-lux-silver/55"
          data-testid="help-safety-link"
        >
          <MessageSquare className="h-5 w-5 text-lux-silver" aria-hidden="true" />
          <p className="mt-4 font-serif text-2xl text-lux-text">Safety guidance</p>
          <p className="mt-2 text-sm text-lux-muted">What verification covers, red flags to watch for and what to do if something isn't right.</p>
          <span className="mt-5 inline-flex items-center gap-1 text-sm text-lux-silver group-hover:text-lux-silverBright">
            Read the guide <ArrowRight className="h-4 w-4" />
          </span>
        </Link>
        <Link
          to="/advertiser/login"
          className="lux-card group block p-6 transition-colors hover:border-lux-silver/55"
          data-testid="help-advertiser-link"
        >
          <Mail className="h-5 w-5 text-lux-silver" aria-hidden="true" />
          <p className="mt-4 font-serif text-2xl text-lux-text">For advertisers</p>
          <p className="mt-2 text-sm text-lux-muted">Sign in to open a support ticket, submit an appeal or update your profile. Verification is handled from your dashboard.</p>
          <span className="mt-5 inline-flex items-center gap-1 text-sm text-lux-silver group-hover:text-lux-silverBright">
            Advertiser area <ArrowRight className="h-4 w-4" />
          </span>
        </Link>
      </section>
    </div>
  );
}
